/**
 * Typed wrappers for the concept-badge endpoints. The backend is
 * the source of truth — frontend never invents badge codes; it just
 * surfaces what's awarded.
 *
 * Frontend-reported events go through `/me/badges/event` with a
 * curated event-name set. The server maps each event to a single
 * badge_code; a stale frontend that POSTs an unknown event gets a
 * 422 with the allowed list.
 */
import { apiBaseUrl, AuthApiError } from '../account/authApi';

export type BadgeTrigger = 'backend_observed' | 'frontend_reported';

export interface BadgeDef {
  code: string;
  name: string;
  description: string;
  why_it_matters: string;
  icon: string;
  trigger: BadgeTrigger;
}

export interface UserBadge {
  code: string;
  name: string;
  description: string;
  why_it_matters: string;
  icon: string;
  awarded_at: string;
  acknowledged_at: string | null;
  context: Record<string, unknown> | null;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${apiBaseUrl()}${path}`;
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: 'include',
  });
  if (response.status === 204) return undefined as T;
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // empty
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null;
    throw new AuthApiError(
      response.status,
      detail,
      `Request to ${path} failed (${response.status}).`,
    );
  }
  return body as T;
}

export async function getBadgeCatalogue(): Promise<BadgeDef[]> {
  return request<BadgeDef[]>('/badges/catalogue');
}

export async function getMyBadges(args: { unacknowledged?: boolean } = {}): Promise<UserBadge[]> {
  const params = new URLSearchParams();
  if (args.unacknowledged) params.set('unacknowledged', 'true');
  const query = params.toString();
  return request<UserBadge[]>(`/me/badges${query ? `?${query}` : ''}`);
}

export async function acknowledgeBadge(code: string): Promise<void> {
  return request<void>(`/me/badges/${encodeURIComponent(code)}/acknowledge`, { method: 'POST' });
}

export async function reportBadgeEvent(event: string): Promise<UserBadge[]> {
  return request<UserBadge[]>('/me/badges/event', {
    method: 'POST',
    body: JSON.stringify({ event }),
  });
}

/** Fire-and-forget version. Swallows 401s (kid signed out) and
 *  network failures so studio actions never block on the badge
 *  endpoint. The toast widget separately invalidates the
 *  unacknowledged-badges query, so a successful award still surfaces
 *  on the next render. */
export function fireBadgeEvent(event: string): void {
  void reportBadgeEvent(event).catch((err) => {
    if (err instanceof AuthApiError && err.status === 401) return;
    // Anything else: log + drop. The badge can fire again later.
    console.warn('[badges] event report failed', event, err);
  });
}
